import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import * as _ from 'lodash';
import { Annotation } from '../models/annotation';
import { AthleteMetrics, Person } from '../models/athlete-metrics';
import { Conference, Sport } from '../models/conference';
import { DownloadRequest } from '../models/downloads';
import { ClipInfo, Game, GameFolder, GameManagerMatchupGroup, GamePlaylistReference, GameRoster } from '../models/game';
import { GradingCell, GradingGrade, GradingGroup, GradingPlayerGroup, GradingPosition, GradingStrategy, GradingString } from '../models/grading';
import { TeamLibraryFile } from '../models/medialibrary';
import { HubHighlightRecord } from '../models/hub';
import { Cutup, Playlist, PlaylistClip } from '../models/playlist';
import { College, CollegeCoachTwitter, Recruiter, RecruitingContact, RecruitmentAttribute, RecruitmentShare, RecruitmentTeam } from '../models/recruitment';
import { AiScoutSavedReportMetadata, AiScoutScoutType, AiScoutSelectedScoutTeam, QwikReportCatalog, QwikReportSelection } from '../models/ai-scout';
import { Report, ReportTemplate, StatReport, StatReportGame } from '../models/reports';
import { EmployeeStatPreset, FootballStatPlaylistContext, GameStats, HockeyStat, StatColumn, StatColumnPreset, StatNote, StatNotePreset } from '../models/stats';
import { AutoHighlightSettings, CustomGrade, Team, TeamGroup, TeamNotificationSettings, TeamSettings, UserAutoHighlightSettings } from '../models/team';
import { SavedCard, User, UserNotificationSettings, UserProfile, UserRoleValue } from '../models/user';
import { Reaction } from '../models/chat';
import { Angle, Clip } from '../models/clip';
import { AvailableHighlightMusicItem, Highlight, HighlightClip } from '../models/highlight';
import { Organization } from '../models/organization';
import { HudlPlaybookImportResult, PlaybookClip, PlaybookFieldSize, PlaybookFolder, PlaybookPlay, PlaybookPlayType, PlaybookScript, PlaybookTemplate, PlaybookType } from '../models/playbook';
import { StripePayment, StripePaymentExtraInfo } from '../models/payments';
import { BreakdownPurchase, BreakdownRequest, IntercutRequest } from '../models/breakdowns';
import { School } from '../models/school';
import { Field, FieldPhoto, Region } from '../models/locations';
import { ShooterAssignment, ShooterInfo, WeatherDelay } from '../models/shooters';
import { TeamPersonnel } from '../models/gamepersonnel';
import { GameRating, LeaguePool } from '../models/leaguepool';
import { EmployeeDocument, Tutorial } from '../models/admin';
import { FanSubscription, FanTournament } from '../models/fans';
import { Product } from '../models/product';
import { SingleInputDialog } from '../components/dialogs/single-input-dialog/single-input-dialog.component';
import type { PhoneticSpellingDialogData } from '../components/dialogs/phonetic-spelling-dialog/phonetic-spelling-dialog.component';
import { Conversation } from '../models/chat';
import { CamopNote } from 'src/app/models/shooters';
import { BillboardPost } from '../models/billboard';
import { QwikcutCamera, QwikcutCameraRelease, QwikcutCameraUpdateEvent } from '../models/qwikcut-camera';
import { Tournament } from '../models/showcase';
import { ThirdParty, ThirdPartyTournament, ThirdPartyTournamentRequest } from '../models/third_parties';
import { InvitationCampaign } from '../models/invitation_campaigns';
import { Presentation, PresentationFolder } from '../models/presentation';
import { MaxPrepsContest, MaxPrepsSchool } from '../models/maxpreps';
import { LocalStorageService } from './local-storage.service';
import { ExternalExchangeGameDraft } from '../models/exchangerequest';
import type { PlayerGradingCellDialogData } from '../components/dialogs/player-grading-cell-dialog/player-grading-cell-dialog.component';
import type { PlayerGradingManageDialogData } from '../components/dialogs/player-grading-manage-dialog/player-grading-manage-dialog.component';
import type { PlayerGradingGroupDialogData } from '../components/dialogs/player-grading-group-dialog/player-grading-group-dialog.component';
import type { PlayerGradingArchivedDialogData } from '../components/dialogs/player-grading-archived-dialog/player-grading-archived-dialog.component';
import { ExchangeDirectoryContact, ExchangeDirectoryCorrection, ExchangeDirectoryCorrectionTarget, ExchangeDirectorySchool } from '../models/exchange-directory';
import { EmployeeTrimGuardAction, EmployeeTrimsDialogData } from '../models/employee-trim-draft';
import {CamOpGroup} from '../models/camop-group';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  private onSubmit = new Subject<any>();

  constructor(
    private dialog: MatDialog,
    private localStorageService: LocalStorageService
  ) {}

  readonly useBlur: boolean = true;

  listenForSubmit(): Observable<any> {
		return this.onSubmit.asObservable();
  }

  emitSubmit() {
    this.onSubmit.next(true);
  }

  async openHighlightMusicDialog(): Promise<AvailableHighlightMusicItem> {
    const dialogName = 'highlight-music-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );

    const dialogComponent = chunk['HighlightMusicDialogComponent'] as  ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.panelClass = 'highlight-music-dialog-panel';
    dialogConfig.data = {};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openHighlightPreviewDialog(highlight: Highlight): Promise<any> {
    const dialogName = 'highlight-preview-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['HighlightPreviewDialogComponent'] as  ComponentType<unknown>;
    if(!dialogComponent) { return; }
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.panelClass = ['highlight-dialog-panel', 'highlight-preview-dialog-panel'];
    dialogConfig.width = 'min(96vw, 1120px)';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = 'calc(100vh - 32px)';
    dialogConfig.data = {
      highlight
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openHighlightAddVideoDialog(hideTeamLibrary?: boolean, customButtonText?: string, customToolTipText?: string, darkerBackground: boolean = false, limit?: number): Promise<HighlightClip[]> {
    const dialogName = 'highlight-add-video-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['HighlightAddVideoDialogComponent'] as  ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = darkerBackground ? ['highlight-add-video-dialog-panel', 'playbook-dialog-panel'] : 'highlight-add-video-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.width = 'min(92vw, 1728px)';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.height = 'min(86vh, 984px)';
    dialogConfig.data = {
      hideTeamLibrary,
      customButtonText,
      customToolTipText,
      limit
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAutoHighlightSettingsDialog(teamid: string, sportid: Sport, isFemaleTeam: boolean = false, mode: 'team' | 'user' = 'team'): Promise<AutoHighlightSettings | UserAutoHighlightSettings> {
    const dialogName = 'auto-highlight-settings-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['AutoHighlightSettingsDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.panelClass = 'auto-highlight-settings-dialog-panel';
    // dialogConfig.width = 'min(96vw, 960px)';
    // dialogConfig.maxWidth = '96vw';
    // dialogConfig.maxHeight = '94vh';
    dialogConfig.data = {
      teamid,
      sportid,
      isFemaleTeam,
      mode,
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAutoHighlightIntroDialog(): Promise<boolean> {
    const dialogName = 'auto-highlight-intro-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['AutoHighlightIntroDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'auto-highlight-intro-dialog-panel';
    dialogConfig.width = 'min(92vw, 600px)';
    dialogConfig.maxWidth = '92vw';
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openInstantAutoHighlightDialog(data: {teamid: string, sportid: string, isFemaleTeam?: boolean, mode?: 'team' | 'user'}): Promise<Highlight> {
    const dialogName = 'instant-auto-highlight-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['InstantAutoHighlightDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'instant-auto-highlight-dialog-panel';
    dialogConfig.width = 'min(96vw, 980px)';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = '94vh';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openConfirmationDialog(body: string, title: string = '', confirmText: string = 'Confirm', cancelText: string = 'Cancel', darkerBackground: boolean = false): Promise<boolean> {
    const dialogName = 'confirmation-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (darkerBackground) dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      body,
			confirmText,
			cancelText
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlaybookInstallGroupDropDialog(installName: string, playCount: number): Promise<'ungrouped' | 'newGroup' | null> {
    const dialogName = 'playbook-install-group-drop-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['PlaybookInstallGroupDropDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = ['playbook-dialog-panel', 'playbook-install-group-drop-dialog-panel'];
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '440px';
    dialogConfig.maxWidth = 'calc(100vw - 32px)';
    dialogConfig.data = {installName, playCount};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEmployeeTrimsDialog(data: EmployeeTrimsDialogData): Promise<EmployeeTrimGuardAction | void> {
    const dialogName = 'employee-trims-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['EmployeeTrimsDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = data.mode === 'guard';
    if (data.mode === 'review') dialogConfig.panelClass = 'employee-trims-dialog-panel';
    dialogConfig.width = data.mode === 'review' ? 'min(94vw, 700px)' : 'min(94vw, 560px)';
    dialogConfig.maxWidth = '94vw';
    dialogConfig.maxHeight = '90vh';
    if (data.mode === 'review' && data.rows.length > 8) dialogConfig.height = 'min(88vh, 760px)';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlayerGradingManageDialog(data: PlayerGradingManageDialogData): Promise<boolean> {
    const dialogName = 'player-grading-manage-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['PlayerGradingManageDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.panelClass = 'player-grading-manage-dialog';
    dialogConfig.width = 'min(96vw, 1400px)';
    dialogConfig.height = 'calc(100vh - 32px)';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = 'calc(100vh - 32px)';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlayerGradingArchivedDialog(data: PlayerGradingArchivedDialogData): Promise<boolean> {
    const dialogName = 'player-grading-archived-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['PlayerGradingArchivedDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.panelClass = 'player-grading-archived-dialog';
    dialogConfig.width = 'min(92vw, 760px)';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.maxHeight = 'calc(100vh - 32px)';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlayerGradingCellDialog(data: PlayerGradingCellDialogData): Promise<GradingCell> {
    const dialogName = 'player-grading-cell-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['PlayerGradingCellDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.panelClass = 'player-grading-cell-dialog';
    dialogConfig.width = 'min(96vw, 1200px)';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = 'calc(100vh - 32px)';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlayerGradingGroupDialog(data: PlayerGradingGroupDialogData): Promise<GradingPlayerGroup> {
    const dialogName = 'player-grading-group-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['PlayerGradingGroupDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.panelClass = 'player-grading-group-dialog';
    dialogConfig.width = 'min(92vw, 720px)';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.maxHeight = 'calc(100vh - 32px)';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }
  
  async openManageExchangeDirectorySchoolDialog(school?: ExchangeDirectorySchool): Promise<ExchangeDirectorySchool> {
    const dialogName = 'manage-exchange-directory-school-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['ManageExchangeDirectorySchoolDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'exchange-directory-editor-dialog-panel';
    dialogConfig.width = 'min(92vw, 720px)';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.data = {school};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageExchangeDirectoryContactDialog(
    school: ExchangeDirectorySchool,
    contact?: ExchangeDirectoryContact
  ): Promise<ExchangeDirectoryContact> {
    const dialogName = 'manage-exchange-directory-contact-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['ManageExchangeDirectoryContactDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'exchange-directory-editor-dialog-panel';
    dialogConfig.width = 'min(92vw, 760px)';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.data = {school, contact};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageTeamExchangeDirectoryContactDialog(
    school?: ExchangeDirectorySchool,
    contact?: ExchangeDirectoryContact
  ): Promise<ExchangeDirectoryContact> {
    const dialogName = 'manage-exchange-directory-contact-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['ManageExchangeDirectoryContactDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'exchange-directory-editor-dialog-panel';
    dialogConfig.width = 'min(92vw, 760px)';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.maxHeight = '92vh';
    dialogConfig.data = {school, contact, teamContact: true};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSubmitExchangeDirectoryCorrectionDialog(
    targetType: ExchangeDirectoryCorrectionTarget,
    target: ExchangeDirectorySchool | ExchangeDirectoryContact
  ): Promise<ExchangeDirectoryCorrection> {
    const dialogName = 'submit-exchange-directory-correction-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['SubmitExchangeDirectoryCorrectionDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'exchange-directory-correction-dialog-panel';
    dialogConfig.width = 'min(92vw, 680px)';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.maxHeight = '92vh';
    dialogConfig.data = {targetType, target};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openReviewExchangeDirectoryCorrectionDialog(
    correction: ExchangeDirectoryCorrection,
    status: 'approved' | 'rejected'
  ): Promise<ExchangeDirectoryCorrection> {
    const dialogName = 'review-exchange-directory-correction-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['ReviewExchangeDirectoryCorrectionDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'exchange-directory-correction-dialog-panel';
    dialogConfig.width = 'min(94vw, 960px)';
    dialogConfig.maxWidth = '94vw';
    dialogConfig.maxHeight = '92vh';
    dialogConfig.data = {correction, status};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageFormationPerspectiveDialog(data: any): Promise<any> {
    const dialogName = 'manage-formation-perspective-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['ManageFormationPerspectiveDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = 'min(92vw, 600px)';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openFormationPerspectiveHelpDialog(): Promise<void> {
    const dialogName = 'formation-perspective-help-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['FormationPerspectiveHelpDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.panelClass = 'formation-perspective-help-dialog-panel';
    dialogConfig.width = 'min(94vw, 760px)';
    dialogConfig.maxWidth = '94vw';
    dialogConfig.maxHeight = '92vh';
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlaylistReferencesWarningDialog(playlistReferences: GamePlaylistReference[]): Promise<boolean> {
    const dialogName = 'playlist-references-warning-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['PlaylistReferencesWarningDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      playlistReferences
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openHighlightVideoDialog(highlight: HubHighlightRecord): Promise<string> {
    const dialogName = 'highlight-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.panelClass = 'highlight-dialog-panel';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = 'calc(100vh - 32px)';
    dialogConfig.disableClose = false;
    dialogConfig.data = {
      highlight
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openUserHubSettingsDialog(user: User, metrics: AthleteMetrics): Promise<string> {
    const dialogName = 'user-hub-settings-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.width = '90vw';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = '92vh';
    dialogConfig.panelClass = 'user-hub-settings-dialog-panel';
    dialogConfig.data = {
      user: user,
      metrics: metrics
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCamopNotesDialog(gameId: string): Promise<CamopNote[]> {
    const dialogName = 'camop-note-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.data = {
      gameId,
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAdminNotesDialog(gameId: string) {
    const dialogName = 'admin-notes-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.data = { gameId };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSimpleInputDialog(title: string, display: string, value?: string | number, type: 'text' | 'number' = 'text', darkerBackground: boolean = false): Promise<string> {
    const dialogName = 'simple-input-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (darkerBackground) dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      display,
      value,
      type
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPhoneticSpellingDialog(data: PhoneticSpellingDialogData, darkerBackground: boolean = false): Promise<string> {
    const dialogName = 'phonetic-spelling-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['PhoneticSpellingDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (darkerBackground) dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSingleInputDialog(
    singleInputDialog: SingleInputDialog
  ): Promise<string> {
    const dialogName = 'single-input-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = singleInputDialog;
    return firstValueFrom(
      this.dialog.open(dialogComponent, dialogConfig).afterClosed()
    );
  }

  openNewCategoryDialog(newCategoryDialog: {
    value?: string;
    type?: 'text' | 'number';
    validator: (value: string) => boolean;
  }): Promise<string> {
    return this.openSingleInputDialog({
      ...newCategoryDialog,
      title: 'New Category',
      display: 'Category Name',
      errorMessage: 'Name has been used for another Category',
    });
  }

  openEditCategoryDialog(newCategoryDialog: {
    value?: string;
    type?: 'text' | 'number';
    validator: (value: string) => boolean;
  }): Promise<string> {
    return this.openSingleInputDialog({
      ...newCategoryDialog,
      title: 'New Category',
      display: 'Category Name',
      errorMessage: 'Name has been used for another Category',
    });
  }

  async openGradingGroupDialog({
    title,
    value,
    type = 'text',
    groupList = [],
  }: {
    title: string;
    value?: string | number;
    type?: 'text' | 'number';
    groupList: GradingGroup[];
  }): Promise<string> {
    const dialogName = 'grading-group-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      value,
      type,
      groupList,
    };
    return firstValueFrom(
      this.dialog.open(dialogComponent, dialogConfig).afterClosed()
    );
  }

  async openGradingPositionDialog({
    title,
    positionName,
    type = 'text',
    groupList = [],
    selectedGroupId,
    positions = [],
  }: {
    title: string;
    positionName?: string | number;
    type?: 'text' | 'number';
    groupList: GradingGroup[];
    selectedGroupId: string;
    positions: GradingPosition[];
  }): Promise<{positionName: string, selectedGroupId: string}> {
    const dialogName = 'grading-position-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      positionName,
      type,
      groupList,
      selectedGroupId,
      positions,
    };
    return firstValueFrom(
      this.dialog.open(dialogComponent, dialogConfig).afterClosed()
    );
  }

  async openGradingStringDialog({
    title,
    value,
    type = 'text',
    stringList = [],
  }: {
    title: string;
    value?: string | number;
    type?: 'text' | 'number';
    stringList: GradingString[];
  }): Promise<string> {
    const dialogName = 'grading-string-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      value,
      type,
      stringList,
    };
    return firstValueFrom(
      this.dialog.open(dialogComponent, dialogConfig).afterClosed()
    );
  }

  async openSimpleDateInputDialog(title: string, display: string, value?: Date, includeTime?: boolean): Promise<Date> {
    const dialogName = 'simple-date-input-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      display,
      value,
			includeTime
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openTextBoxDialog(title: string, display: string, value?: string, readonly: boolean = false): Promise<string> {
    const dialogName = 'text-box-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      display,
      value,
      readonly
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSimpleDropdownDialog(title: string, display: string, options: string[], value?: string, allowCustom?: boolean): Promise<string> {
    const dialogName = 'simple-dropdown-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      display,
      options,
      value,
      allowCustom
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSimpleDropdownMultiDialog(title: string, display: string, options: {display: string, value: string}[], values: string[] = []): Promise<string[]> {
    const dialogName = 'simple-dropdown-multi-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title,
      display,
      options,
			values
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openForgotPasswordDialog(email?: string): Promise<any> {
    const dialogName = 'forgot-password-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.data = email;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCreateGameDialog(user: User, seasonid: number, createEvent: boolean = false, parentfolderid?: string, hudlImport: boolean = false, teamid?: string, opponent?: string): Promise<Game> {
    const dialogName = 'create-game-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '880px';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.data = {
      user,
      seasonid,
      createEvent,
      parentfolderid,
      hudlImport,
			teamid,
			opponent
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditGameDialog(user: User, game: Game): Promise<Game> {
    const dialogName = 'edit-game-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      game
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openDownloadGameDialog(user: User, games: Game[]): Promise<DownloadRequest> {
    const dialogName = 'download-game-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      games
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openFolderSelectDialog(user: User, darkerBackground: boolean = false): Promise<GameFolder> {
    const dialogName = 'folder-select-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (darkerBackground) dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlaylistSelectDialog(user: User): Promise<Playlist[]> {
    const dialogName = 'playlist-select-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openGameSelectDialog(user: User, teamid?: string, maxGamesSelected?: number, expandToGame?: Game, selectedGames?: Game[], darkerBackground: boolean = false): Promise<Game[]> {
    const dialogName = 'game-select-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (darkerBackground) dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      teamid,
      maxGamesSelected,
      expandToGame,
      selectedGames
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAiScoutSourcePickerDialog(data: {user: User; teamid?: string; selectedSeasonid?: number; scoutType?: AiScoutScoutType; selectedScoutTeams?: AiScoutSelectedScoutTeam[]}): Promise<AiScoutSelectedScoutTeam[]> {
    const dialogName = 'ai-scout-source-picker-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['AiScoutSourcePickerDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'ai-scout-source-picker-dialog-panel';
    dialogConfig.width = '1280px';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.height = 'min(800px, 80vh)';
    dialogConfig.maxHeight = '80vh';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAiScoutQwikReportsDialog(data: {catalog: QwikReportCatalog; selection?: QwikReportSelection}): Promise<QwikReportSelection> {
    const dialogName = 'ai-scout-qwik-reports-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['AiScoutQwikReportsDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'ai-scout-qwik-reports-dialog-panel';
    dialogConfig.width = '920px';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.height = 'min(760px, 86vh)';
    dialogConfig.maxHeight = '86vh';
    dialogConfig.data = data;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openHudlImportDialog(user: User, game: Game): Promise<boolean> {
    const dialogName = 'hudl-import-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '680px';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.data = {
      user,
      game
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openHudlImportSeasonDialog(user: User, teamid: string, sportid: Sport): Promise<boolean> {
    const dialogName = 'hudl-import-season-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
			teamid,
			sportid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportMultipleGamesDialog(user: User, teamid: string, sportid: Sport, seasonid: number, parentfolderid: string): Promise<boolean> {
    const dialogName = 'import-multiple-games-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
			teamid,
			sportid,
			seasonid,
			parentfolderid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageFolderDialog(user: User, seasonid: number, folder?: GameFolder, parentfolderid?: string): Promise<GameFolder> {
    const dialogName = 'manage-folder-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      seasonid,
      folder,
      parentfolderid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManagePlaylistDialog(user: User, seasonid?: number, playlist?: Playlist, parentfolderid?: string, clips?: PlaylistClip[]): Promise<Playlist> {
    const dialogName = 'manage-playlist-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      seasonid,
      playlist,
      parentfolderid,
      clips
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openViewPlaylistDialog(playlist: Playlist): Promise<any> {
    const dialogName = 'view-playlist-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      playlist
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openFootballStatPlaylistDialog(context: FootballStatPlaylistContext): Promise<any> {
    const dialogName = 'football-stat-playlist-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['FootballStatPlaylistDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.width = '94vw';
    dialogConfig.maxWidth = '1440px';
    dialogConfig.height = '88vh';
    dialogConfig.maxHeight = '920px';
    dialogConfig.panelClass = 'football-stat-playlist-dialog-panel';
    dialogConfig.data = {context};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageStatPresetsDialog(teamid: string, sportid: Sport, statPresets: StatColumnPreset[], statColumns: StatColumn[]): Promise<StatColumnPreset[]> {
    const dialogName = 'manage-stat-presets-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '80vw';
    dialogConfig.maxWidth = '80vw';
    dialogConfig.data = {
      teamid,
      sportid,
      statPresets,
      statColumns
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageStatNotePresetsDialog(sportid: Sport, presets: StatNotePreset[]): Promise<StatNotePreset[]> {
    const dialogName = 'manage-stat-note-presets-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '640px';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.data = {
      sportid,
      presets
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportBreakdownDialog(teamid: string, gameid: string, breakdownIsCsv: boolean): Promise<GameStats> {
    const dialogName = 'import-breakdown-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      gameid,
      breakdownIsCsv
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportBreakdownContinuousDialog(teamid: string, gameid: string, sportid: Sport): Promise<GameStats> {
    const dialogName = 'import-breakdown-continuous-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      gameid,
      sportid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageCustomReportDialog(user: User, statColumns: StatColumn[], report?: Report): Promise<Report> {
    const dialogName = 'manage-custom-report-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      statColumns,
      report
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageSavedStatReportDialog(seasonid: number, games: StatReportGame[], playlistid?: string, statName?: string, reportOn?: string[], filters?: {name: string, values: any[]}[], originalSavedReport?: StatReport, defaultName?: string, aiScout?: AiScoutSavedReportMetadata): Promise<StatReport> {
    const dialogName = 'manage-saved-stat-report-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[1] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      seasonid,
			games,
			playlistid,
			statName,
			reportOn,
			filters,
      originalSavedReport,
      defaultName,
      aiScout
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageTeamUserDialog(userRoles: UserRoleValue[], teamid?: string, originalUser?: User, sportid?: Sport, leaguepoolid?: string, payForAccess?: boolean, parentPrice?: number, thirdpartyid?: string, currentSeasonid?: number): Promise<User> {
    const dialogName = 'manage-team-user-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      userRoles,
      originalUser,
      sportid,
      leaguepoolid,
			payForAccess,
      parentPrice,
			thirdpartyid,
      currentSeasonid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageTeamGroupDialog(teamid: string, coaches: User[], athletes: User[], parents: User[], originalGroup?: TeamGroup): Promise<TeamGroup> {
    const dialogName = 'manage-team-group-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      coaches,
      athletes,
      parents,
      originalGroup
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageAnnotationsDialog(user: User, gameid: string, type: 'game' | 'clip' | 'group', clipnumber?: number, angle?: Angle, groupTime?: number): Promise<Annotation> {
    const dialogName = 'manage-annotations-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      gameid,
			type,
			clipnumber,
			angle,
			groupTime
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openStatAdjustmentDialog(teamid: string, gameid: string, playernumber: number, columnName: string, originalValue: number, adjustedValue: number): Promise<{adjustedValue: number, adjustmentAmount: number}> {
    const dialogName = 'stat-adjustment-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      gameid,
      playernumber,
      columnName,
      originalValue,
      adjustedValue
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAthleteMetricsDialog(userid: string, teamid: string, sportid: Sport, elevatedPriv: boolean, metricsid?: string, isAdmin: boolean = false, coachData?: Person): Promise<AthleteMetrics> {
    const dialogName = 'athlete-metrics-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      userid,
      teamid,
      sportid,
      elevatedPriv,
			metricsid,
			isAdmin,
			coachData
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageRecruitingAttributesDialog(teamid: string): Promise<RecruitmentAttribute[]> {
    const dialogName = 'manage-recruiting-attributes-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openRecruitmentDialog(teamid: string, user: User, athlete: User): Promise<RecruitmentShare> {
    const dialogName = 'recruitment-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      user,
      athlete
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openRecruitingContactDialog(user: User, originalContact?: RecruitingContact): Promise<RecruitingContact> {
    const dialogName = 'manage-recruiting-contact-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      originalContact
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCustomGradeDialog(teamid: string, gradingStrategies: GradingStrategy[], defaultGrade: boolean, defaultStrategyId: string, originalCustomGrade?: CustomGrade): Promise<TeamSettings> {
    const dialogName = 'custom-grade-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      gradingStrategies,
      defaultGrade,
      defaultStrategyId,
      originalCustomGrade
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openGradingScaleDialog(teamid: string, originalGradingScale?: GradingStrategy): Promise<GradingStrategy> {
    const dialogName = 'grading-scale-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      originalGradingScale
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageBreakdownDataDialog(teamid: string, sportid: Sport, existingStatColumns: StatColumn[], originalColumn?: StatColumn): Promise<StatColumn[]> {
    const dialogName = 'manage-breakdown-data-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      sportid,
      existingStatColumns,
      originalColumn
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openUploadMediaLibraryFilesDialog(teamid: string): Promise<TeamLibraryFile[]> {
    const dialogName = 'upload-media-library-files-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openViewedClipDialog(userid: string): Promise<undefined> {
    const dialogName = 'viewed-clips-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '1200px';
    dialogConfig.height = '94vh';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = '94vh';
    dialogConfig.panelClass = 'viewed-clips-dialog-panel';
    dialogConfig.data = {
      userid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openTeamViewingAnalyticsDialog(teamSettings?: TeamSettings): Promise<undefined> {
    const dialogName = 'team-viewing-analytics-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '1400px';
    dialogConfig.height = '94vh';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = '94vh';
    dialogConfig.panelClass = 'team-viewing-analytics-dialog-panel';
    dialogConfig.data = {teamSettings};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportUsersDialog(teamid: string, sportid: Sport, role: UserRoleValue, parentPrice?: number, currentSeasonid?: number): Promise<User[]> {
    const dialogName = 'import-users-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      sportid,
			role,
			parentPrice,
      currentSeasonid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditPersonnelDialog(teamid: string, roster: User[], selectedAthletes: number[], limit?: number, includeUnknown: boolean = false, seasonid?: number): Promise<{roster: User[], selectedAthletes: number[]}> {
    const dialogName = 'edit-personnel-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      roster,
      selectedAthletes,
      limit,
      includeUnknown,
      seasonid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditContinuousStatDialog(stat: any, homeRoster: User[], awayRoster: User[], game: Game): Promise<any> {
    const dialogName = 'edit-continuous-stat-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      stat,
      homeRoster,
      awayRoster,
      game
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openChangeAngleOrderDialog(angles: Angle[]): Promise<Angle[]> {
    const dialogName = 'change-angle-order-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      angles
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openWrestlingWinDialog(): Promise<number> {
    const dialogName = 'wrestling-win-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditClipInfoDialog(game: Game, clipInfo?: ClipInfo, homeRoster?: User[], awayRoster?: User[]): Promise<ClipInfo> {
    const dialogName = 'edit-clip-info-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      game,
      clipInfo,
      homeRoster,
      awayRoster
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageConferenceDialog(conference?: Conference): Promise<Conference> {
    const dialogName = 'manage-conference-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      conference
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageOrganizationDialog(organization?: Organization): Promise<Organization> {
    const dialogName = 'manage-organization-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      organization
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageTeamDialog(team?: Team): Promise<Team> {
    const dialogName = 'manage-team-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      team
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSharePlaybookFolderDialog(user: User, folder: PlaybookFolder): Promise<{userids: string[], groupids: string[]}> {
    const dialogName = 'share-playbook-folder-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      folder
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSendPlaybookPlaysDialog(user: User, plays: PlaybookPlay[], install?: PlaybookFolder): Promise<any> {
    const dialogName = 'send-playbook-plays-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      plays,
      install
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditScriptColumnsDialog(script: PlaybookScript): Promise<PlaybookScript> {
    const dialogName = 'edit-script-columns-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      script
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openViewPlaybookClipsDialog(clips: PlaybookClip[]): Promise<any> {
    const dialogName = 'view-playbook-clips-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      clips
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManagePlaybookFolderDialog(teamid: string, type: PlaybookPlayType, playids?: string[], folder?: PlaybookFolder): Promise<PlaybookFolder> {
    const dialogName = 'manage-playbook-folder-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			teamid,
			type,
			playids,
			folder
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openDownloadPlaylistDialog(playlist: Playlist): Promise<any> {
    const dialogName = 'download-playlist-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      playlist
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAddCardDialog(user: User, buttonText?: string): Promise<String> {
    const dialogName = 'add-card-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      buttonText
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCollectNonUserPaymentDialog(email: string, amount: number, description: string, paymentType: number, extraInfo?: StripePaymentExtraInfo, buttonText?: string): Promise<StripePayment> {
    const dialogName = 'collect-non-user-payment-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      email,
      amount,
      description,
      paymentType,
      extraInfo,
      buttonText
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openMakePaymentDialog(user: User, amount: number, description: string, type: number, extrainfo?: StripePaymentExtraInfo): Promise<StripePayment> {
    const dialogName = 'make-payment-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      amount,
      description,
      type,
      extrainfo
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openRequestBreakdownDialog(user: User, game: Game, teamid?: string, teamlevelid?: string): Promise<{breakdownRequest?: BreakdownRequest, intercutRequest?: IntercutRequest}> {
    const dialogName = 'request-breakdown-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '1200px';
    dialogConfig.maxWidth = '94vw';
    dialogConfig.maxHeight = '92vh';
    dialogConfig.data = {
      user,
      game,
      teamid,
      teamlevelid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPurchaseBreakdownPlanDialog(user: User, sportid: Sport, team: Team, isAdmin?: boolean): Promise<BreakdownPurchase> {
    const dialogName = 'purchase-breakdown-plan-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      sportid,
      team,
      isAdmin
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageBreakdownPlansDialog(user: User, team: Team): Promise<undefined> {
    const dialogName = 'manage-breakdown-plans-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      team
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditBreakdownPlanDialog(purchase: BreakdownPurchase, sportid: Sport): Promise<BreakdownPurchase> {
    const dialogName = 'edit-breakdown-plan-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			purchase,
			sportid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAssignmentGamesDialog(assignment: ShooterAssignment): Promise<undefined> {
    const dialogName = 'assignment-games-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'assignment-games-dialog-panel';
    dialogConfig.width = '1120px';
    dialogConfig.maxWidth = '94vw';
    dialogConfig.maxHeight = '92vh';
    dialogConfig.data = {
      assignment
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAdminManageUserDialog(user: User): Promise<User> {
    const dialogName = 'manage-user-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageSchoolOrganizationsDialog(school: School): Promise<School> {
    const dialogName = 'manage-school-organizations-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      school
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageSchoolDialog(school?: School): Promise<School> {
    const dialogName = 'manage-school-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      school
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openLinkOrganizationToSchoolDialog(schoolid: string): Promise<Organization> {
    const dialogName = 'link-organization-to-school-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      schoolid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSyncSchoolsToConferenceDialog(): Promise<boolean> {
    const dialogName = 'sync-schools-to-conference-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageFieldDialog(field?: Field): Promise<Field> {
    const dialogName = 'manage-field-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      field
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageRegionDialog(region?: Region): Promise<Region> {
    const dialogName = 'manage-region-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      region
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageCamopDialog(camop?: ShooterInfo): Promise<ShooterInfo> {
    const dialogName = 'manage-camop-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      camop
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageCamopGroupDialog(group: CamOpGroup, conferences: Conference[], managers: ShooterInfo[]): Promise<CamOpGroup> {
    const dialogName = 'manage-camop-group-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['ManageCamOpGroupDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {group, conferences, managers};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openGameInfoDialog(game: Game, breakdownRequest: BreakdownRequest): Promise<undefined> {
    const dialogName = 'game-info-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      game,
      breakdownRequest
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openViewStatNotesDialog(statNotes: StatNote[], isEmployee: boolean = false): Promise<number> {
    const dialogName = 'view-stat-notes-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      statNotes,
      isEmployee
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openFormationSelectDialog(previousBackfield?: string, previousFormation?: string, playbookSelection: boolean = false, fieldSize?: PlaybookFieldSize): Promise<{formation: string, backfield: string, play?: PlaybookPlay}> {
    const dialogName = 'formation-select-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (playbookSelection) dialogConfig.panelClass = 'playbook-dialog-panel';
    else dialogConfig.position = {right: '10px', bottom: '10px'};

    dialogConfig.disableClose = true;
    dialogConfig.data = {
      previousBackfield,
      previousFormation,
      showTeamMapping: playbookSelection,
      fieldSize
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openTeamSelectDialog(sport?: Sport, includeFilters: boolean = false, allowMultiSelect: boolean = false): Promise<Team[]> {
    const dialogName = 'team-select-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      sport,
      includeFilters,
      allowMultiSelect
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openUserSelectDialog(teamid?: string, userRoles?: UserRoleValue[], multiple: boolean = false): Promise<User[]> {
    const dialogName = 'user-select-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
			userRoles,
			multiple
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAdminMfaResetDialog(user: User): Promise<boolean> {
    const dialogName = 'admin-mfa-reset-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = 'min(94vw, 560px)';
    dialogConfig.data = {user};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageCutupDialog(user: User, cutup: Cutup): Promise<undefined> {
    const dialogName = 'manage-cutup-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      cutup
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openBaseballBaserunnersDialog(baseRunnerInfo: {playernumber: number, newbase: number}[], athletes: User[], teamid: string, seasonid: number): Promise<{playernumber: number, newbase: number}[]> {
    const dialogName = 'baseball-baserunners-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      baseRunnerInfo,
      athletes,
      teamid,
      seasonid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openBaseballGameFinishedDialog(game: Game, homeTeamPersonnel: TeamPersonnel, awayTeamPersonnel: TeamPersonnel, homeRoster: User[], awayRoster: User[], homeTeamWinning: boolean): Promise<{homeTeamPersonnel: TeamPersonnel, awayTeamPersonnel: TeamPersonnel}> {
    const dialogName = 'baseball-game-finished-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      game,
      homeTeamPersonnel,
      awayTeamPersonnel,
      homeRoster,
      awayRoster,
      homeTeamWinning
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageStorageDialog(team: Team): Promise<undefined> {
    const dialogName = 'manage-storage-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      team
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCamopScheduleGameDialog(user: User): Promise<ShooterAssignment> {
    const dialogName = 'camop-schedule-game-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openTableDialog(tablename: string, columns: {display: string, value: string}[], rows: any[]): Promise<undefined> {
    const dialogName = 'table-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      tablename,
      columns,
      rows
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditLeaguePoolDialog(pool?: LeaguePool): Promise<LeaguePool> {
    const dialogName = 'edit-league-pool-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      pool
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageLeaguePoolTeamsDialog(user: User, leaguePool: LeaguePool): Promise<undefined> {
    const dialogName = 'manage-league-pool-teams-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
			leaguePool
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAddOrgsToLeaguePoolDialog(leaguepoolid: string): Promise<undefined> {
    const dialogName = 'add-orgs-to-league-pool-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      leaguepoolid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openGameViewsDialog(game: Game): Promise<undefined> {
    const dialogName = 'game-views-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      game
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openRateVideoDialog(game: Game, gameRating?: GameRating): Promise<Game> {
    const dialogName = 'rate-video-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      game,
      gameRating
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditTeamOffenseDefenseDialog(team: Team): Promise<Team> {
    const dialogName = 'edit-team-offense-defense-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      team
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openLeaguePoolMessageDialog(leaguepoolid: string): Promise<undefined> {
    const dialogName = 'league-pool-message-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      leaguepoolid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openLeaguePoolSettingsDialog(leaguepoolid: string): Promise<LeaguePool> {
    const dialogName = 'league-pool-settings-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      leaguepoolid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openMatchupGamesDialog(group: GameManagerMatchupGroup): Promise<GameManagerMatchupGroup> {
    const dialogName = 'matchup-games-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      group
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openGamesInfoDialog(gameids: string[]): Promise<undefined> {
    const dialogName = 'games-info-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      gameids
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAdminEditGameDialog(game?: Game, sportid?: Sport, fieldid?: string, subfield?: string, gameDate?: Date, seasonid?: number): Promise<Game> {
    const dialogName = 'admin-edit-game-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      game,
      sportid,
      fieldid,
      subfield,
      gameDate,
      seasonid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openConfirmGamesDialog(user: User, games: Game[]): Promise<Game[]> {
    const dialogName = 'confirm-games-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      games
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAdjustCamopPayDialog(assignmentids: string[], amount?: number, reason?: string, explanation?: string): Promise<{amount: number, reason: string, explanation: string} | null> {
    const dialogName = 'adjust-camop-pay-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      assignmentids,
      amount,
      reason,
      explanation
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageTutorialDialog(originalTutorial?: Tutorial): Promise<Tutorial> {
    const dialogName = 'manage-tutorial-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      originalTutorial
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageFanRevenueDialog(team: Team): Promise<undefined> {
    const dialogName = 'manage-fan-revenue-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      team
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openFilmReviewVideoDialog(currentClip: Clip, allClips: Clip[], userRole: string = ''): Promise<undefined> {
    const dialogName = 'film-review-video-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      currentClip,
      allClips,
      userRole
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCamopFilmReviewDialog(userid: string): Promise<undefined> {
    const dialogName = 'camop-filmreview-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      userid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditMatchupDateDialog(date: Date, timeDifference?: number): Promise<{date: Date, timeDifference: number}> {
    const dialogName = 'edit-matchup-date-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      date,
      timeDifference
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageEmployeeDocumentDialog(document?: EmployeeDocument): Promise<EmployeeDocument> {
    const dialogName = 'manage-employee-document-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      document
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageRecruiterDialog(originalRecruiter?: Recruiter): Promise<Recruiter> {
    const dialogName = 'manage-recruiter-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      originalRecruiter
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportRecruitersDialog(leads: boolean = false): Promise<boolean> {
    const dialogName = 'import-recruiters-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      leads
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageCollegeDialog(originalCollege?: College): Promise<College> {
    const dialogName = 'manage-college-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      originalCollege
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportCollegesDialog(): Promise<boolean> {
    const dialogName = 'import-colleges-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSelectCollegeDialog(twitterOnly: boolean = true): Promise<{college: College, athlete: User}> {
    const dialogName = 'select-college-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      twitterOnly
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSelectRecruitersDialog(showAll: boolean = false): Promise<{recruiters: Recruiter[], athlete: User}> {
    const dialogName = 'select-recruiters-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      showAll
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageProductDialog(originalProduct?: Product): Promise<Product> {
    const dialogName = 'manage-product-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      originalProduct
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openBuildProspectSheetDialog(): Promise<boolean> {
    const dialogName = 'build-prospect-sheet-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openRecruitmentEmailDialog(user: User, athlete: User, highlight: Highlight, recruiters: Recruiter[]): Promise<RecruitmentShare> {
    const dialogName = 'recruitment-email-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      athlete,
      highlight,
      recruiters
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openGameRosterDialog(teamid: string, gameid: string, seasonid: number): Promise<GameRoster> {
    const dialogName = 'game-roster-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			teamid,
			gameid,
      seasonid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openVideoEmbedDialog(embed: string): Promise<undefined> {
    const dialogName = 'video-embed-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			embed
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportProspectsDialog(sportid?: Sport): Promise<boolean> {
    const dialogName = 'import-prospects-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			sportid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openUploadFieldPhotoDialog(fieldid: string): Promise<FieldPhoto> {
    const dialogName = 'upload-field-photo-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			fieldid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openViewFieldPhotosDialog(fieldid: string): Promise<FieldPhoto> {
    const dialogName = 'view-field-photos-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			fieldid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openClipNotesDialog(
    athlete: User,
    userId: string,
    clipNumber: number,
    currentGame: GradingGrade,
    coaches: User[],
    canManageAudio: boolean = true,
  ): Promise<GradingGrade> {
    const dialogName = 'clip-notes-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'player-notes-dialog-panel';
    dialogConfig.width = 'min(92vw, 900px)';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.height = 'min(76vh, 700px)';
    dialogConfig.maxHeight = 'calc(100vh - 32px)';
    dialogConfig.data = {
      athlete: athlete,
      userId: userId,
      clipNumber: clipNumber,
      currentGame: currentGame,
      coaches: coaches,
      canManageAudio: canManageAudio,
    };
    return firstValueFrom(
      this.dialog.open(dialogComponent, dialogConfig).afterClosed()
    );
  }

  async openShooterNotesDialog(notes : CamopNote[], elevatedPriv: boolean, role: string) {
    const dialogName = 'shooter-notes-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.data = {
      notes,
      elevatedPriv,
      role,
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPreviewSetUpPhotoDialog(photoUrl: string, locationGps: string) {
    const dialogName = 'preview-setup-photo-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.data = {photoUrl, locationGps};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCamopDialog(camopIds: string[], camOpObject: Record<ShooterInfo['userid'], ShooterInfo>, camOps: ShooterInfo[]) {
    const dialogName = 'camop-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.data = {
      camopIds,
      camOpObject,
      camOps,
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }
	async openCreateConversationDialog(teamId: string, users: User[], teamGroups: TeamGroup[]): Promise<Conversation> {
		const dialogName = 'conversation-dialog';
		const chunk = await import(
			`../components/dialogs/${dialogName}/${dialogName}.component`
		);
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
		dialogConfig.data = { title: 'New Conversation', teamId, users, teamGroups };
			return firstValueFrom(
				this.dialog.open(dialogComponent, dialogConfig).afterClosed()
			);
	}

  async openWeatherDelaysDialog(weatherDelays: WeatherDelay[] = []): Promise<WeatherDelay[] | null> {
    const dialogName = 'weather-delays-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
		dialogConfig.data = { weatherDelays };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditConversationDialog(teamId: string, users: User[], teamGroups: TeamGroup[], conversation: Conversation): Promise<Conversation> {
    const dialogName = 'conversation-dialog';
		const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
		dialogConfig.data = { title: 'Edit Conversation', teamId, users, teamGroups, conversation };
    return firstValueFrom(
      this.dialog.open(dialogComponent, dialogConfig).afterClosed()
    );
	}

  async openCreateBillboardPostDialog(): Promise<BillboardPost> {
    const dialogName = 'billboard-post-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title: 'Create Billboard Post',
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditBillboardPostDialog(post: BillboardPost): Promise<BillboardPost> {
    const dialogName = 'billboard-post-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title: 'Edit Billboard Post',
      post: post,
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openEditBillboardPostContentDialog(content: string, type: string): Promise<BillboardPost> {
    const dialogName = 'billboard-post-content-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      content,
      type,
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

	async openShareGameDialog(user: User, games: Game[]): Promise<{shareUserids: string[], shareGroupids: string[]}> {
    const dialogName = 'share-game-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			user,
			games
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPreviewReactionDialog(reaction: Reaction, messageId: string, listUsersName: any): Promise<any> {
    const dialogName = 'preview-reaction-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );

    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.data = {reaction, messageId, listUsersName};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openScheduleMessageDialog(conversationId: string): Promise<any> {
    const dialogName = 'schedule-message-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );

    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.data = {conversationId};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openUploadCameraReleaseDialog(release?: QwikcutCameraRelease): Promise<QwikcutCameraRelease> {
    const dialogName = 'upload-camera-software-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );

    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {release};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCameraReleaseEventsDialog(release: QwikcutCameraRelease, events: QwikcutCameraUpdateEvent[]): Promise<void> {
    const dialogName = 'camera-release-events-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );

    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.data = {release, events};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageTeamNotificationsDialog(): Promise<TeamNotificationSettings> {
    const dialogName = 'manage-team-notifications-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );

    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.data = {};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageUserNotificationsDialog(userid: string, sportid: Sport): Promise<UserNotificationSettings> {
    const dialogName = 'manage-user-notifications-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );

    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.data = {userid, sportid};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageParentAbilitiesDialog(teamid: string, sportid: Sport): Promise<TeamSettings> {
    const dialogName = 'manage-parent-abilities-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );

    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.data = {teamid, sportid};
		return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }
	
  async openManageTournamentDialog(tournament?: Tournament): Promise<Tournament> {
    const dialogName = 'manage-tournament-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      tournament
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openUserRolesDialog(): Promise<undefined> {
    const dialogName = 'user-roles-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openStatisticianRequestsDialog(statistician: User, sportid: Sport, rangeStart: Date, rangeEnd: Date): Promise<undefined> {
    const dialogName = 'statistician-requests-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			statistician,
			sportid,
			rangeStart,
			rangeEnd
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageThirdPartyDialog(originalThirdParty?: ThirdParty): Promise<ThirdParty> {
    const dialogName = 'manage-third-party-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {originalThirdParty};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }
	
  async openManageThirdPartyTournamentDialog(thirdPartyId: string, sports: Sport[] = [], tournament?: ThirdPartyTournament): Promise<ThirdPartyTournament> {
    const dialogName = 'manage-thirdparty-tournament-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			thirdPartyId,
			sports,
      tournament
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCompleteThirdPartyTournamentPurchaseDialog(request: ThirdPartyTournamentRequest, tournament: ThirdPartyTournament): Promise<{user: User, request: ThirdPartyTournamentRequest}> {
    const dialogName = 'complete-third-party-tournament-purchase-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			request,
      tournament
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openThirdpartyTournamentGameInfoDialog(request: ThirdPartyTournamentRequest): Promise<any> {
    const dialogName = 'thirdparty-tournament-game-info-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			request
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openTeamMetricsDialog(team: Team): Promise<undefined> {
    const dialogName = 'team-metrics-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			team
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openThirdPartyTournamentLinkDialog(tournamentid: string): Promise<undefined> {
    const dialogName = 'third-party-tournament-link-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			tournamentid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageCoachTwitterDialog(originalCoachTwitter?: CollegeCoachTwitter): Promise<CollegeCoachTwitter> {
    const dialogName = 'manage-coach-twitter-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			originalCoachTwitter
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportCoachTwittersDialog(): Promise<boolean> {
    const dialogName = 'import-coach-twitters-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageInvitationCampaignDialog(originalCampaign?: InvitationCampaign): Promise<InvitationCampaign> {
    const dialogName = 'manage-invitation-campaign-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			originalCampaign
		};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCompleteInvitationCampaignDialog(campaign: InvitationCampaign, preview: boolean = false): Promise<InvitationCampaign> {
    const dialogName = 'complete-invitation-campaign-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			campaign,
			preview
		};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCameraSeamDialog(cameraId: string): Promise<any> {
    const dialogName = 'camera-seam-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['CameraSeamDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = false;
    dialogConfig.width = 'min(620px, 94vw)';
    dialogConfig.maxWidth = '94vw';
    dialogConfig.data = {cameraId};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageQwikcutCameraInformationDialog(camera?: QwikcutCamera): Promise<QwikcutCamera> {
    const dialogName = 'manage-qwikcut-camera-info-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
		if (camera) dialogConfig.autoFocus = false;
    dialogConfig.data = {
			camera
		};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManagerUserProfileDialog(userid: string): Promise<UserProfile> {
    const dialogName = 'manage-user-profile-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			userid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlaybookHotkeysDialog(): Promise<any> {
    const dialogName = 'playbook-hotkeys-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openViewPlaybookPlayDialog(play: PlaybookPlay, script?: PlaybookScript, elevatedPriv: boolean = true): Promise<any> {
    const dialogName = 'view-playbook-play-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {play, script, elevatedPriv};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManagePlaybookScriptDialog(teamid: string, seasonid?: number, script?: PlaybookScript, qwikScript: boolean = false): Promise<PlaybookScript> {
    const dialogName = 'manage-playbook-script-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			teamid,
      seasonid,
      script,
      qwikScript
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openQwikScriptAddPlaysDialog(script: PlaybookScript): Promise<PlaybookScript> {
    const dialogName = 'qwik-script-add-plays-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      script
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlaybookInstallExternalSharingDialog(teamid: string, install: PlaybookFolder): Promise<PlaybookFolder> {
    const dialogName = 'playbook-install-external-sharing-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      install
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlaybookFolderSelectDialog(user: User, teamid: string, titleText?: string, confirmText?: string): Promise<PlaybookFolder> {
    const dialogName = 'playbook-folder-select-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      teamid,
      titleText,
      confirmText
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManagePlaybookTemplateDialog(template: PlaybookTemplate): Promise<PlaybookTemplate> {
    const dialogName = 'manage-playbook-template-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      template
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openInstallPlayerActivityDialog(install?: PlaybookFolder, script?: PlaybookScript): Promise<undefined> {
    const dialogName = 'playbook-install-player-activity-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      install,
      script
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlaybookTutorialsDialog(startingIdx?: number, autoPlay?: boolean): Promise<undefined> {
    const dialogName = 'playbook-tutorials-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      startingIdx,
      autoPlay
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openHudlPlaybookImportDialog(teamid: string): Promise<HudlPlaybookImportResult> {
    const chunk = await import(
      '../components/playbook/hudl-playbook-import/hudl-playbook-import.component'
    );
    const dialogComponent = chunk['HudlPlaybookImportComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = 'min(96vw, 1560px)';
    dialogConfig.maxWidth = '96vw';
    dialogConfig.maxHeight = '96vh';
    dialogConfig.data = {teamid};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openHudlPlaybookPlayEditorDialog(play: PlaybookPlay): Promise<PlaybookPlay> {
    const dialogName = 'hudl-playbook-play-editor-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = 'min(98vw, 1800px)';
    dialogConfig.maxWidth = '98vw';
    dialogConfig.maxHeight = '96vh';
    dialogConfig.data = {play};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openChangeGameJerseyNumbersDialog(gameid: string, teamidToChange: string, teamid?: string): Promise<boolean> {
    const dialogName = 'change-game-jersey-numbers-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      gameid,
      teamidToChange,
      teamid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openPlaybookPlaySelectDialog(user: User, teamid: string, limit?: number): Promise<PlaybookPlay[]> {
    const dialogName = 'playbook-play-select-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      user,
      teamid,
      limit
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManagePresentationFolderDialog(folder?: PresentationFolder): Promise<PresentationFolder> {
    const dialogName = 'manage-presentation-folder-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			folder
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openUploadPresentationFileDialog(presentationid: string, slideid: string): Promise<{name: string, src: string, type: 'video' | 'audio' | 'image'}> {
    const dialogName = 'upload-presentation-file-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			presentationid,
      slideid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSendPresentationDialog(presentation: Presentation): Promise<Presentation> {
    const dialogName = 'send-presentation-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'playbook-dialog-panel';
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
			presentation
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageReportTemplateDialog(statColumns: StatColumn[], reportTemplate?: ReportTemplate): Promise<ReportTemplate> {
    const dialogName = 'manage-report-template-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      statColumns,
			reportTemplate
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCreateExternalExchangeGameDialog(
    sportid: Sport,
    hudlImport?: boolean,
    exchangeRequestId?: string,
    exchangeAccessToken?: string,
    breakdownRequested?: boolean
  ): Promise<ExternalExchangeGameDraft> {
    const dialogName = 'create-external-exchange-game-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.width = '680px';
    dialogConfig.maxWidth = '92vw';
    dialogConfig.data = {
      sportid,
      hudlImport,
      exchangeRequestId,
      exchangeAccessToken,
      breakdownRequested
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportRecruitmentTeamsDialog(): Promise<boolean> {
    const dialogName = 'import-recruitment-teams-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {};
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageRecruitmentTeamDialog(originalTeam?: RecruitmentTeam): Promise<RecruitmentTeam> {
    const dialogName = 'manage-recruitment-team-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      originalTeam
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportScheduleDialog(teamid: string, seasonid?: number): Promise<Game[]> {
    const dialogName = 'import-schedule-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid,
      seasonid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSelectMaxprepsSchoolDialog(defaultStateid?: string, searchText?: string): Promise<MaxPrepsSchool> {
    const dialogName = 'select-maxpreps-school-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      defaultStateid,
      searchText
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openImportMaxPrepsScheduleDialog(teamid: string): Promise<number> {
    const dialogName = 'import-maxpreps-schedule-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openSelectMaxprepsGameDialog(teamid: string): Promise<MaxPrepsContest> {
    const dialogName = 'select-maxpreps-game-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      teamid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openManageEmployeeStatPresetDialog(preset: EmployeeStatPreset, statColumns: StatColumn[]): Promise<EmployeeStatPreset> {
    const dialogName = 'manage-employee-stat-preset-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      preset,
      statColumns
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openFootballStatChangesDialog(gameid: string): Promise<any> {
    const dialogName = 'football-stat-changes-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      gameid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openAddScoreDifferentialDialog(game: Game, stat: HockeyStat, homeRoster: User[], awayRoster: User[], limit?: number): Promise<{homeRoster: User[], goalPlayersHome: number[], awayRoster: User[], goalPlayersAway: number[]}> {
    const dialogName = 'add-score-differential-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    // if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.position = {right: '0', top: '5vh'};
    dialogConfig.data = {
      game,
      stat,
      homeRoster,
      awayRoster,
      limit
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openCopyContinuousStatsDialog(): Promise<any> {
    const dialogName = 'copy-continuous-stats-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openViewContinuousQcStatEditsDialog(gameid: string): Promise<number> {
    const dialogName = 'view-continuous-qc-stat-edits-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = Object.values(chunk)[0] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      gameid
    };
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }

  async openHofAdDialog(): Promise<'remind-later' | 'dont-show-again' | null> {
    if (this.localStorageService.getHofAdDismissed()) return null;
    const dialogName = 'hof-ad-dialog';
    const chunk = await import(
      `../components/dialogs/${dialogName}/${dialogName}.component`
    );
    const dialogComponent = chunk['HofAdDialogComponent'] as ComponentType<unknown>;
    const dialogConfig = new MatDialogConfig();
    if (this.useBlur) dialogConfig.backdropClass = 'backdrop-blur';
    dialogConfig.panelClass = 'hof-ad-dialog-panel';
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = false;
    dialogConfig.restoreFocus = false;
    return firstValueFrom(this.dialog.open(dialogComponent, dialogConfig).afterClosed());
  }
}
